import React from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Euro,
  Eye,
  EyeOff,
  GripVertical,
  Info,
  Truck,
} from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Link } from 'react-router-dom';
import { DatePicker } from '../../../components/DatePicker';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { DraggableOfferRow } from './OfferDraggableRow';
import { OfferRowImageUpload } from './OfferRowImageUpload';
import { OfferUnitPicker } from './OfferUnitPicker';
import { OfferCalculationRowActions } from './OfferCalculationRowActions';
import type {
  LeadPosition,
  OfferDraftImagePayload,
  OfferDraftRow,
  OfferingPositionComponent,
  PositionRowId,
} from '../../../types';
import { formatGermanNumber, parseGermanNumber } from '../../../utils/parseGermanNumber';
import {
  aluminumQuoteFieldLabel,
  copperQuoteFieldLabel,
  fetchCurrentWestmetallQuote,
  METAL_QUOTE_SOURCE_LABELS,
  formatMetalSurchargeHint,
  visibleMetalSurchargeHint,
  type MetalQuoteSource,
} from '../../../utils/metalQuotes';
import { SETTINGS_PATH_INTEGRATION_METAL } from '../../../utils/settingsRoutes';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { EVENTUAL_POSITION_HINT, isOfferMetaRowKind } from '../utils/offerEventualPosition';
import {
  isHourLaborPricedRow,
  isVirtualTitleLaborRow,
  MINUTES_PER_LABOR_HOUR,
  parseHourlyRateOverride,
  patchForUnitChange,
} from '../utils/offerLaborPricing';
import { marginPercent, surchargeForVk, vkFromEkSurcharge } from '../utils/offerPricing';
import {
  computeOfferCalculationRowFigures,
  computeOfferCalculationSummary,
  computeOfferCalculationTitleTotals,
  emptyTitleTotals,
  type OfferCalculationTitleTotals,
} from '../utils/offerCalculationSummary';
import {
  groupOfferMarginHints,
  type OfferMarginHint,
  type OfferMarginHintGroup,
} from '../utils/offerMarginHints';
import { titleIndentPx } from '../utils/offerTitleHierarchy';
import {
  CALCULATION_ROW_OVERSCAN_PX,
  CALCULATION_VIRTUALIZE_MIN_ROWS,
  visibleCalculationRowWindow,
  type CalculationRowWindow,
} from '../utils/calculationRowWindow';

type SidebarCardKey = 'hints' | 'summary' | 'positions' | 'rates';

/** So lange bleibt eine angesprungene Zeile markiert. */
const ROW_HIGHLIGHT_MS = 2500;

const COLLAPSED_CARDS_STORAGE_KEY = 'offerCalculationSidebarCollapsed';

function readCollapsedCards(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_CARDS_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeCollapsedCards(value: Record<string, boolean>) {
  try {
    window.localStorage.setItem(COLLAPSED_CARDS_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Speichern ist optional — ohne localStorage bleibt der Zustand nur für diese Sitzung.
  }
}

export type OfferCalculationRow = {
  id: string;
  kind: OfferDraftRow['kind'];
  leadPositionId: PositionRowId | null;
  sourceType: LeadPosition['source_type'] | null;
  quantity: number;
  unit: string;
  item_name: string;
  description: string;
  unit_price_vk: number;
  unit_cost_ek: number;
  surcharge_percent: number;
  labor_minutes: number;
  hourly_rate?: number | null;
  isApi: boolean;
  isEventual: boolean;
  image?: OfferDraftImagePayload | null;
  numberLabel?: string;
  titleLevel?: number | null;
  rowDepth?: number;
  costKind?: OfferDraftRow['costKind'];
  components?: OfferingPositionComponent[];
  metal_kind?: 'cu' | 'al' | null;
  metal_surcharge?: number | null;
  metal_priced_ek?: number | null;
};

function uglComponentRoleLabel(
  role: OfferingPositionComponent['ugl_role'],
  surchargeType: string | null,
): string | null {
  if (role === 'labor') return 'Lohn';
  if (role === 'surcharge') return surchargeType?.trim() || 'Zuschlag';
  return null;
}

export type OfferCalculationPatch = Partial<
  Pick<
    OfferCalculationRow,
    | 'quantity'
    | 'unit'
    | 'item_name'
    | 'description'
    | 'unit_price_vk'
    | 'unit_cost_ek'
    | 'surcharge_percent'
    | 'labor_minutes'
    | 'hourly_rate'
  >
>;

export type MetalQuoteValues = {
  source: MetalQuoteSource;
  copper: number | null;
  aluminum: number | null;
  quoteDate: string | null;
  procurementCostPercent: number;
};

type Props = {
  rows: OfferCalculationRow[];
  readOnly?: boolean;
  onCommit: (row: OfferCalculationRow, patch: OfferCalculationPatch) => void;
  hourlyRate: number;
  realLaborCostRate: number;
  onHourlyRateCommit: (value: number) => void;
  onRealLaborCostRateCommit: (value: number) => void;
  copperQuoteEurPer100kg: number | null;
  aluminumQuoteEurPer100kg: number | null;
  metalQuoteSource: MetalQuoteSource;
  metalQuoteDate: string | null;
  metalProcurementCostPercent: number;
  instanceMetalQuoteSource: MetalQuoteSource;
  instanceMetalQuoteDate: string | null;
  /** Metallnotierungen werden gesammelt gespeichert, nicht Feld für Feld. */
  onMetalQuotesCommit: (values: MetalQuoteValues) => void;
  metalQuotesSaving?: boolean;
  listLaborSeparately?: boolean;
  onListLaborSeparatelyChange?: (value: boolean) => void;
  onToggleEventual?: (row: OfferCalculationRow) => void;
  onImageUpload?: (row: OfferCalculationRow, file: File) => void;
  onDeleteRow?: (row: OfferCalculationRow) => void;
  isRowDeleting?: (row: OfferCalculationRow) => boolean;
  sidebarContent?: React.ReactNode;
  /** Suchpanel braucht eine hohe Box, die Bausteine-Liste bleibt kompakt. */
  sidebarNeedsHeight?: boolean;
  tableRef?: React.Ref<HTMLDivElement>;
  renderDropBand?: (index: number, columnCount: number) => React.ReactNode;
  /** Lieferantenangebote je Positions-ID (nur bei Vorgängen aus einer Ausschreibung). */
  quoteOffersByPositionId?: Record<string, QuoteOffersSummary>;
  onOpenQuoteOffers?: (row: OfferCalculationRow) => void;
  /** Sichtbare Margen-Hinweise (ohne ausgeblendete), berechnet auf genau diesen `rows`. */
  marginHints?: OfferMarginHint[];
  /** „Ausblenden“: IDs der Hinweise; ohne Handler gibt es den Button nicht. */
  onDismissMarginHints?: (ids: string[]) => void;
};

/** Kompakte Sicht auf die Angebote einer Position für die Pill neben dem EK. */
export type QuoteOffersSummary = {
  count: number;
  cheapestEk: number | null;
  cheapestSupplier: string | null;
  appliedSupplier: string | null;
};

const CELL_FOCUS_CLASS =
  'outline-none focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20';

function estimateCalculationRowPx(
  row: OfferCalculationRow,
  hideDescriptions: boolean,
  expanded: boolean,
): number {
  if (row.kind === 'image') return 132;
  if (row.kind === 'title') return 48;
  if (row.kind === 'text') return 64;
  let height = hideDescriptions ? 48 : 88;
  if (expanded && (row.components?.length ?? 0) > 0) {
    height += 32 + (row.components?.length ?? 0) * 22;
  }
  return height;
}

type CalculationWindowApi = CalculationRowWindow & {
  measureRow: (id: string, part: 'main' | 'extra', node: HTMLTableRowElement | null) => void;
};

/** „Zur Position“: Zeile ins Bild holen; `nonce` erlaubt denselben Index zweimal. */
type CalculationScrollRequest = { index: number; nonce: number };

function assignScrollRef(tableRef: React.Ref<HTMLDivElement> | undefined, node: HTMLDivElement | null) {
  if (typeof tableRef === 'function') tableRef(node);
  else if (tableRef) (tableRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
}

/** Scroll-Fenster der Kalkulationstabelle. Summen bleiben draußen, nur dieses Stück rendert Zeilen. */
function CalculationVirtualBody({
  tableRef,
  rows,
  hideDescriptions,
  expandedIds,
  scrollRequest,
  children,
}: {
  tableRef?: React.Ref<HTMLDivElement>;
  rows: OfferCalculationRow[];
  hideDescriptions: boolean;
  expandedIds: ReadonlySet<string>;
  scrollRequest?: CalculationScrollRequest | null;
  children: (api: CalculationWindowApi) => React.ReactNode;
}) {
  const localRef = React.useRef<HTMLDivElement | null>(null);
  const virtualize = rows.length >= CALCULATION_VIRTUALIZE_MIN_ROWS;
  const [scrollTop, setScrollTop] = React.useState(0);
  const [viewportHeight, setViewportHeight] = React.useState(640);
  const [headerHeight, setHeaderHeight] = React.useState(40);
  const [bandHeight, setBandHeight] = React.useState(12);
  const [layoutVersion, setLayoutVersion] = React.useState(0);
  const heightsRef = React.useRef(new Map<string, number>());
  const partsRef = React.useRef(new Map<string, { main: number; extra: number }>());
  const nodesRef = React.useRef(new Map<string, HTMLTableRowElement | null>());
  const observersRef = React.useRef(new Map<string, ResizeObserver>());
  const frameRef = React.useRef(0);

  const setScrollRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      localRef.current = node;
      assignScrollRef(tableRef, node);
    },
    [tableRef],
  );

  React.useEffect(() => {
    heightsRef.current.clear();
    partsRef.current.clear();
    setLayoutVersion((version) => version + 1);
  }, [hideDescriptions]);

  React.useEffect(() => {
    const el = localRef.current;
    if (!el) return;
    const measureChrome = () => {
      const nextHeight = el.clientHeight;
      if (nextHeight > 0) setViewportHeight((prev) => (prev === nextHeight ? prev : nextHeight));
      const headH = el.querySelector('thead')?.getBoundingClientRect().height ?? 0;
      if (headH > 0) setHeaderHeight((prev) => (prev === headH ? prev : headH));
      const bandH = el.querySelector<HTMLElement>('[data-offer-drop-index]')?.offsetHeight ?? 0;
      if (bandH > 0) setBandHeight((prev) => (prev === bandH ? prev : bandH));
    };
    measureChrome();
    const observer = new ResizeObserver(measureChrome);
    observer.observe(el);
    return () => observer.disconnect();
  }, [rows.length, virtualize]);

  const publishHeight = React.useCallback((id: string) => {
    const parts = partsRef.current.get(id);
    const total = (parts?.main ?? 0) + (parts?.extra ?? 0);
    if (total <= 0) return;
    const previous = heightsRef.current.get(id);
    if (previous != null && Math.abs(previous - total) <= 1) return;
    heightsRef.current.set(id, total);
    setLayoutVersion((version) => version + 1);
  }, []);

  const measureRow = React.useCallback(
    (id: string, part: 'main' | 'extra', node: HTMLTableRowElement | null) => {
      if (!virtualize) return;
      const key = `${id}:${part}`;
      const previous = nodesRef.current.get(key) ?? null;
      if (previous === node) return;
      nodesRef.current.set(key, node);
      observersRef.current.get(key)?.disconnect();
      observersRef.current.delete(key);
      const parts = partsRef.current.get(id) ?? { main: 0, extra: 0 };
      if (!node) {
        parts[part] = 0;
        partsRef.current.set(id, parts);
        publishHeight(id);
        return;
      }
      const read = () => {
        const next = node.offsetHeight;
        const current = partsRef.current.get(id) ?? { main: 0, extra: 0 };
        if (current[part] === next) return;
        current[part] = next;
        partsRef.current.set(id, current);
        publishHeight(id);
      };
      read();
      const observer = new ResizeObserver(read);
      observer.observe(node);
      observersRef.current.set(key, observer);
    },
    [publishHeight, virtualize],
  );

  React.useEffect(
    () => () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      for (const observer of observersRef.current.values()) observer.disconnect();
    },
    [],
  );

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!virtualize) return;
    const top = event.currentTarget.scrollTop;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      setScrollTop(top);
    });
  };

  const heights = React.useMemo(
    () =>
      rows.map((row) => {
        const measured = heightsRef.current.get(row.id);
        return bandHeight + (measured ?? estimateCalculationRowPx(row, hideDescriptions, expandedIds.has(row.id)));
      }),
    [bandHeight, expandedIds, hideDescriptions, layoutVersion, rows],
  );

  // Zur Position: im virtuellen Fenster erst grob hinscrollen, damit die Zeile gerendert wird,
  // danach (nächster Effekt-Durchlauf mit der Zeile im DOM) genau zentrieren.
  const pendingScrollIndexRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!scrollRequest || rows.length === 0) return;
    const index = Math.max(0, Math.min(rows.length - 1, scrollRequest.index));
    pendingScrollIndexRef.current = index;
    const el = localRef.current;
    if (!virtualize || !el) return;
    let offset = 0;
    for (let i = 0; i < index; i += 1) offset += heights[i] ?? 0;
    const target = Math.max(0, headerHeight + offset - viewportHeight / 3);
    el.scrollTop = target;
    setScrollTop(target);
    // Auch ohne Scroll-Änderung neu rendern, damit der Effekt unten die Zeile findet.
    setLayoutVersion((version) => version + 1);
    // Nur auf neue Anfragen reagieren, nicht auf jede Höhenmessung.
  }, [scrollRequest]);
  React.useEffect(() => {
    const index = pendingScrollIndexRef.current;
    if (index == null) return;
    const node = localRef.current?.querySelector<HTMLElement>(`[data-offer-row-index="${index}"]`);
    if (!node) return;
    pendingScrollIndexRef.current = null;
    node.scrollIntoView({ block: 'center', behavior: virtualize ? 'auto' : 'smooth' });
  });

  const rowWindow = virtualize
    ? visibleCalculationRowWindow({
        count: rows.length,
        heights,
        trailing: bandHeight,
        scrollTop,
        viewportHeight,
        headerHeight,
        overscanPx: CALCULATION_ROW_OVERSCAN_PX,
      })
    : { start: 0, end: rows.length, paddingTop: 0, paddingBottom: 0 };

  return (
    <div ref={setScrollRef} onScroll={onScroll} className="app-scrollbar min-h-0 flex-1 overflow-auto">
      {children({ ...rowWindow, measureRow })}
    </div>
  );
}

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const decimal = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function NumericCell({
  value,
  readOnly,
  ariaLabel,
  onCommit,
  maximumFractionDigits = 2,
}: {
  value: number;
  readOnly: boolean;
  ariaLabel: string;
  onCommit: (value: number) => void;
  maximumFractionDigits?: number;
}) {
  const formatValue = React.useCallback(
    (next: number) =>
      formatGermanNumber(next, { minimumFractionDigits: 2, maximumFractionDigits }),
    [maximumFractionDigits],
  );
  const [text, setText] = React.useState(() => formatValue(value));
  React.useEffect(() => setText(formatValue(value)), [formatValue, value]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      disabled={readOnly}
      aria-label={ariaLabel}
      onChange={(event) => setText(event.target.value)}
      onFocus={(event) => {
        // Nullwerte müssen beim Tippen nicht erst gelöscht werden.
        if (parseGermanNumber(text) === 0) setText('');
        else event.target.select();
      }}
      onBlur={() => {
        const next = parseGermanNumber(text);
        setText(formatValue(next));
        if (Math.abs(next - value) > 0.0001) onCommit(next);
      }}
      className={`h-9 w-full min-w-[5rem] rounded-md border border-app-border bg-app-surface px-2 text-right text-sm tabular-nums text-app-text disabled:bg-app-surface-muted disabled:text-app-text-subtle ${CELL_FOCUS_CLASS}`}
    />
  );
}

function TextCell({
  value,
  readOnly,
  ariaLabel,
  onCommit,
  className,
}: {
  value: string;
  readOnly: boolean;
  ariaLabel: string;
  onCommit: (value: string) => void;
  className?: string;
}) {
  const [text, setText] = React.useState(value);
  React.useEffect(() => setText(value), [value]);
  return (
    <input
      type="text"
      value={text}
      disabled={readOnly}
      aria-label={ariaLabel}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        if (text !== value) onCommit(text);
      }}
      className={
        className ??
        `h-9 w-full rounded-md border border-app-border bg-app-surface px-2 text-sm text-app-text disabled:bg-app-surface-muted disabled:text-app-text-subtle ${CELL_FOCUS_CLASS}`
      }
    />
  );
}

function DescriptionCell({
  value,
  readOnly,
  ariaLabel,
  onCommit,
}: {
  value: string;
  readOnly: boolean;
  ariaLabel: string;
  onCommit: (value: string) => void;
}) {
  const [text, setText] = React.useState(value);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  React.useEffect(() => setText(value), [value]);
  React.useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.max(36, el.scrollHeight)}px`;
  }, [text]);

  return (
    <textarea
      ref={textareaRef}
      value={text}
      disabled={readOnly}
      aria-label={ariaLabel}
      rows={1}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        if (text !== value) onCommit(text);
      }}
      className={`min-h-9 w-full resize-none overflow-hidden rounded-md border border-app-border bg-app-surface px-2 py-2 text-sm leading-5 text-app-text disabled:bg-app-surface-muted disabled:text-app-text-subtle ${CELL_FOCUS_CLASS}`}
    />
  );
}

export function OfferCalculationTable({
  rows,
  readOnly = false,
  onCommit,
  hourlyRate,
  realLaborCostRate,
  copperQuoteEurPer100kg,
  aluminumQuoteEurPer100kg,
  metalQuoteSource,
  metalQuoteDate,
  metalProcurementCostPercent,
  instanceMetalQuoteSource,
  instanceMetalQuoteDate,
  onMetalQuotesCommit,
  metalQuotesSaving = false,
  onHourlyRateCommit,
  onRealLaborCostRateCommit,
  listLaborSeparately = false,
  onListLaborSeparatelyChange,
  onToggleEventual,
  onImageUpload,
  onDeleteRow,
  isRowDeleting,
  sidebarContent,
  sidebarNeedsHeight = false,
  tableRef,
  renderDropBand,
  quoteOffersByPositionId,
  onOpenQuoteOffers,
  marginHints,
  onDismissMarginHints,
}: Props) {
  // Metallnotierungen (Integration „metal“) nur, wenn sie für die Instanz an ist.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const metalEnabled = modulesLoaded && isEnabled('metal');
  const showRowActions = !readOnly && Boolean(onDeleteRow || onToggleEventual);
  const columnCount = showRowActions ? 12 : 11;
  /** Nur für diese Ansicht — Kundendokument und PDF bleiben unberührt. */
  const [hideDescriptions, setHideDescriptions] = React.useState(false);
  const [expandedComponentRowIds, setExpandedComponentRowIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [collapsedCards, setCollapsedCards] = React.useState<Record<string, boolean>>(
    readCollapsedCards,
  );
  const isCollapsed = (key: SidebarCardKey) => collapsedCards[key] === true;
  const toggleCard = (key: SidebarCardKey) => {
    setCollapsedCards((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      writeCollapsedCards(next);
      return next;
    });
  };
  const titleTotals = React.useMemo(
    () => computeOfferCalculationTitleTotals(rows, { hourlyRate, realLaborCostRate }),
    [hourlyRate, realLaborCostRate, rows],
  );
  const summary = React.useMemo(
    () => computeOfferCalculationSummary(rows, { hourlyRate, realLaborCostRate }),
    [hourlyRate, realLaborCostRate, rows],
  );
  const hintGroups = React.useMemo(() => groupOfferMarginHints(marginHints ?? []), [marginHints]);
  const hintsByRowId = React.useMemo(() => {
    const map = new Map<string, OfferMarginHint[]>();
    for (const hint of marginHints ?? []) {
      if (!hint.rowId) continue;
      const list = map.get(hint.rowId) ?? [];
      list.push(hint);
      map.set(hint.rowId, list);
    }
    return map;
  }, [marginHints]);
  const [scrollRequest, setScrollRequest] = React.useState<CalculationScrollRequest | null>(null);
  const [highlightRowId, setHighlightRowId] = React.useState<string | null>(null);
  const goToRow = React.useCallback(
    (index: number) => {
      const target = rows[index];
      if (!target) return;
      setScrollRequest((prev) => ({ index, nonce: (prev?.nonce ?? 0) + 1 }));
      setHighlightRowId(target.id);
    },
    [rows],
  );
  React.useEffect(() => {
    if (!highlightRowId) return;
    const timer = window.setTimeout(() => setHighlightRowId(null), ROW_HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [highlightRowId, scrollRequest]);

  return (
    <div className="flex w-full flex-col gap-4 px-2 py-4 xl:h-full xl:min-h-0 xl:flex-row xl:items-stretch xl:pr-4">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-sm xl:min-h-0">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-app-border px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-app-text">Kalkulationsliste</h2>
            <p className="mt-0.5 text-xs text-app-text-subtle">
              {listLaborSeparately
                ? 'Arbeitszeit erscheint als eigene Zeile je Titel — wie im Kundendokument.'
                : 'Material und Lohn getrennt. Der Kunden-EP enthält den Lohnanteil, sobald ein Stundensatz gesetzt ist.'}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {onListLaborSeparatelyChange ? (
            <button
              type="button"
              onClick={() => onListLaborSeparatelyChange(!listLaborSeparately)}
              disabled={readOnly}
              aria-pressed={listLaborSeparately}
              className={`inline-flex h-9 items-center rounded-md px-3.5 text-sm font-medium ${
                listLaborSeparately
                  ? 'border border-blue-200 bg-blue-50 text-blue-700'
                  : 'text-app-text-muted hover:bg-app-hover'
              }`}
            >
              Arbeitszeit separat ausweisen
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setHideDescriptions((v) => !v)}
            aria-pressed={hideDescriptions}
            className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md px-3.5 text-sm font-medium text-app-text-muted hover:bg-app-hover"
          >
            {hideDescriptions ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {hideDescriptions ? 'Beschreibungen zeigen' : 'Beschreibungen ausblenden'}
          </button>
          </div>
        </div>
        <CalculationVirtualBody
          tableRef={tableRef}
          rows={rows}
          hideDescriptions={hideDescriptions}
          expandedIds={expandedComponentRowIds}
          scrollRequest={scrollRequest}
        >
        {({ start, end, paddingTop, paddingBottom, measureRow }) => (
          <table className="w-full min-w-[1280px] border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-app-surface-muted text-left text-xs font-semibold text-app-text-muted">
              <tr>
                <th className="w-16 px-3 py-2">Pos.</th>
                <th className="min-w-[260px] px-3 py-2">Bezeichnung</th>
                <th className="w-24 px-3 py-2 text-right">Menge</th>
                <th className="w-24 px-3 py-2">Einheit</th>
                <th className="w-24 px-3 py-2 text-right">Lohnmin.</th>
                <th className="w-28 px-3 py-2 text-right">Lohnanteil</th>
                <th className="w-28 px-3 py-2 text-right">Material-EK</th>
                <th className="w-24 px-3 py-2 text-right">Aufschlag</th>
                <th className="w-28 px-3 py-2 text-right">Material-VK</th>
                <th className="w-28 px-3 py-2 text-right">Gesamt</th>
                <th className="w-24 px-3 py-2 text-right">Marge</th>
                {showRowActions ? <th className="w-12 px-2 py-2" aria-label="Aktionen" /> : null}
              </tr>
            </thead>
            <tbody>
              {paddingTop > 0 ? (
                <tr aria-hidden style={{ height: paddingTop }}>
                  <td
                    colSpan={columnCount}
                    style={{ height: paddingTop, padding: 0, border: 0, lineHeight: 0, fontSize: 0 }}
                  />
                </tr>
              ) : null}
              {rows.slice(start, end).map((row, offset) => {
                const index = start + offset;
                const isTitle = row.kind === 'title';
                const isMeta = isOfferMetaRowKind(row.kind);
                const isVirtualLabor = isVirtualTitleLaborRow(row);
                const isHourLabor = isHourLaborPricedRow(row);
                const canToggleEventual = Boolean(onToggleEventual) && !isMeta && !isVirtualLabor;
                const canSetHourlyRate = !isMeta && !isVirtualLabor;
                const hourlyRateOverride = parseHourlyRateOverride(row.hourly_rate);
                const { laborShare, lineTotal, margin } = computeOfferCalculationRowFigures(row, {
                  hourlyRate,
                  realLaborCostRate,
                  listLaborSeparately,
                });
                const rowHints = hintsByRowId.get(row.id);
                const highlighted = highlightRowId === row.id;
                const rowReadOnly = readOnly || isVirtualLabor;
                const metalHint =
                  isVirtualLabor || isHourLabor ? null : visibleMetalSurchargeHint(row);
                return (
                  <React.Fragment key={row.id}>
                  {renderDropBand?.(index, columnCount)}
                  <DraggableOfferRow id={`row-${row.id}`} rowId={row.id}>
                    {({ setNodeRef, style, attributes, listeners }) => (
                  <tr
                    ref={(node) => {
                      setNodeRef(node);
                      measureRow(row.id, 'main', node);
                    }}
                    style={style}
                    data-offer-row-index={index}
                    className={`transition-colors duration-500 ${
                      highlighted
                        ? isTitle
                          ? 'border-y border-amber-200 bg-amber-100'
                          : 'bg-amber-50'
                        : isTitle
                          ? 'border-y border-app-border bg-slate-100'
                          : isVirtualLabor
                            ? 'bg-blue-50/50 hover:bg-blue-50'
                            : 'hover:bg-app-hover/50'
                    }`}
                  >
                    <td className="px-3 py-2 align-top font-medium tabular-nums text-app-text-muted">
                      <div className="flex items-center gap-1">
                        {readOnly || isVirtualLabor ? null : (
                          <span
                            {...attributes}
                            {...listeners}
                            className="inline-flex shrink-0 cursor-grab touch-none rounded p-0.5 text-app-text-subtle hover:text-app-text-muted active:cursor-grabbing"
                            aria-label={`Position ${row.numberLabel || row.id} verschieben`}
                          >
                            <GripVertical className="pointer-events-none h-3.5 w-3.5" />
                          </span>
                        )}
                        <span className={isTitle ? 'text-sm font-semibold text-app-text' : undefined}>
                          {row.numberLabel || '—'}
                        </span>
                        {(row.components?.length ?? 0) > 0 ? (
                          <button
                            type="button"
                            onClick={() => {
                              setExpandedComponentRowIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(row.id)) next.delete(row.id);
                                else next.add(row.id);
                                return next;
                              });
                            }}
                            className="inline-flex shrink-0 rounded p-0.5 text-app-text-subtle hover:text-app-text-muted"
                            aria-expanded={expandedComponentRowIds.has(row.id)}
                            aria-label="Stückliste aufklappen"
                          >
                            <ChevronRight
                              className={`h-3.5 w-3.5 transition-transform ${expandedComponentRowIds.has(row.id) ? 'rotate-90' : ''}`}
                            />
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      {row.kind === 'image' ? (
                        <OfferRowImageUpload
                          image={row.image}
                          readOnly={readOnly}
                          variant="dropzone"
                          onUpload={(file) => onImageUpload?.(row, file)}
                        />
                      ) : (
                      <div className="space-y-1.5" style={{ paddingLeft: titleIndentPx(row.rowDepth ?? (row.kind === 'title' ? row.titleLevel : 0)) }}>
                        <TextCell
                          value={row.item_name}
                          readOnly={readOnly}
                          ariaLabel={`Bezeichnung Position ${row.numberLabel || row.id}`}
                          onCommit={(item_name) => onCommit(row, { item_name })}
                          className={
                            isTitle
                              ? `h-9 w-full rounded-md border border-transparent bg-transparent px-2 font-semibold text-app-text disabled:text-app-text ${CELL_FOCUS_CLASS} ${
                                  (row.titleLevel ?? 1) > 1 ? 'text-sm' : 'text-base'
                                }`
                              : undefined
                          }
                        />
                        {!isTitle && row.isEventual ? (
                          <p className="text-xs leading-tight text-app-text-subtle">{EVENTUAL_POSITION_HINT}</p>
                        ) : null}
                        {!isTitle && !isMeta && !isVirtualLabor && !hideDescriptions ? (
                          <DescriptionCell
                            value={row.description}
                            readOnly={readOnly}
                            ariaLabel={`Beschreibung Position ${row.numberLabel || row.id}`}
                            onCommit={(description) => onCommit(row, { description })}
                          />
                        ) : null}
                      </div>
                      )}
                    </td>
                    {isTitle ? (
                      <TitleSubtotalCells
                        totals={titleTotals.get(row.id) ?? emptyTitleTotals()}
                      />
                    ) : isMeta ? (
                      <>
                        <td colSpan={7} />
                        <td className="px-3 py-2 align-top text-right">
                          <DisplayAmount>—</DisplayAmount>
                        </td>
                        <td />
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 align-top">
                          <NumericCell
                            value={row.quantity}
                            readOnly={rowReadOnly}
                            ariaLabel={`Menge Position ${row.numberLabel || row.id}`}
                            onCommit={(quantity) => onCommit(row, { quantity })}
                          />
                        </td>
                        <td className="px-3 py-2 align-top">
                          {isVirtualLabor ? (
                            <span className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface-muted px-2 text-sm text-app-text-muted">
                              {row.unit}
                            </span>
                          ) : (
                          <OfferUnitPicker
                            value={row.unit}
                            disabled={readOnly}
                            triggerClassName="h-9 text-sm"
                            ariaLabel={`Einheit Position ${row.numberLabel || row.id}`}
                            onChange={(unit) => onCommit(row, patchForUnitChange(unit, row.unit))}
                          />
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {isVirtualLabor ? (
                            <DisplayAmount muted>—</DisplayAmount>
                          ) : isHourLabor ? (
                            <DisplayAmount title="Automatisch aus Menge × 60">
                              {decimal.format(row.quantity * MINUTES_PER_LABOR_HOUR)}
                            </DisplayAmount>
                          ) : (
                          <NumericCell
                            value={row.labor_minutes}
                            readOnly={readOnly}
                            ariaLabel={`Lohnminuten Position ${row.numberLabel || row.id}`}
                            onCommit={(labor_minutes) =>
                              onCommit(row, { labor_minutes: Math.max(0, labor_minutes) })
                            }
                          />
                          )}
                        </td>
                        <td className="px-3 py-2 align-top text-right">
                          <DisplayAmount muted={isVirtualLabor}>
                            {isVirtualLabor ? '—' : euro.format(laborShare)}
                          </DisplayAmount>
                          {hourlyRateOverride != null && !isVirtualLabor ? (
                            <div
                              className="mt-0.5 inline-flex w-full items-center justify-end gap-0.5 text-[11px] tabular-nums text-app-text-muted"
                              title={`Abweichender Stundensatz: ${decimal.format(hourlyRateOverride)} €/h`}
                            >
                              <Euro className="h-3 w-3 shrink-0" aria-hidden />
                              {decimal.format(hourlyRateOverride)} €/h
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {isVirtualLabor || isHourLabor ? (
                            <DisplayAmount muted title={isHourLabor ? 'Nicht verwendbar bei Stundenpositionen' : undefined}>—</DisplayAmount>
                          ) : (
                          <NumericCell
                            value={row.unit_cost_ek}
                            readOnly={readOnly}
                            ariaLabel={`Material-EK Position ${row.numberLabel || row.id}`}
                            maximumFractionDigits={metalHint ? 4 : 2}
                            onCommit={(unit_cost_ek) =>
                              onCommit(row, {
                                unit_cost_ek,
                                unit_price_vk: vkFromEkSurcharge(unit_cost_ek, row.surcharge_percent),
                              })
                            }
                          />
                          )}
                          {metalHint ? (
                            <div
                              className="mt-0.5 text-right text-[11px] tabular-nums text-app-text-muted"
                              title="Metallzuschlag je Mengeneinheit, enthalten im Material-EK"
                            >
                              {formatMetalSurchargeHint(metalHint.kind, metalHint.amount)}
                            </div>
                          ) : null}
                          {(() => {
                            const summary =
                              row.isApi && row.leadPositionId != null && quoteOffersByPositionId
                                ? quoteOffersByPositionId[String(row.leadPositionId)]
                                : undefined;
                            if (!summary || summary.count === 0 || !onOpenQuoteOffers) return null;
                            const cheaper = summary.cheapestEk != null && summary.cheapestEk < row.unit_cost_ek - 0.0001;
                            return (
                              <button
                                type="button"
                                onClick={() => onOpenQuoteOffers(row)}
                                className={`mt-0.5 inline-flex w-full items-center justify-end gap-1 rounded px-1 py-0.5 text-[11px] tabular-nums transition hover:bg-app-hover ${
                                  summary.appliedSupplier
                                    ? 'text-emerald-700'
                                    : cheaper
                                      ? 'text-emerald-700'
                                      : 'text-app-text-muted'
                                }`}
                                title={
                                  summary.appliedSupplier
                                    ? `EK aus Angebot ${summary.appliedSupplier}`
                                    : summary.cheapestEk != null
                                      ? `Günstigstes Angebot: ${summary.cheapestSupplier ?? ''} ${euro.format(summary.cheapestEk)}`
                                      : `${summary.count} Angebote`
                                }
                              >
                                <Truck className="h-3 w-3 shrink-0" aria-hidden />
                                {summary.appliedSupplier
                                  ? summary.appliedSupplier
                                  : summary.cheapestEk != null
                                    ? euro.format(summary.cheapestEk)
                                    : `${summary.count} Angebote`}
                              </button>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {isVirtualLabor || isHourLabor ? (
                            <DisplayAmount muted title={isHourLabor ? 'Nicht verwendbar bei Stundenpositionen' : undefined}>—</DisplayAmount>
                          ) : (
                          <NumericCell
                            value={row.surcharge_percent}
                            readOnly={readOnly}
                            ariaLabel={`Aufschlag Position ${row.numberLabel || row.id}`}
                            onCommit={(surcharge_percent) =>
                              onCommit(row, {
                                surcharge_percent,
                                unit_price_vk: vkFromEkSurcharge(row.unit_cost_ek, surcharge_percent),
                              })
                            }
                          />
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {isVirtualLabor ? (
                            <DisplayAmount>{euro.format(row.unit_price_vk)}</DisplayAmount>
                          ) : isHourLabor ? (
                            <DisplayAmount muted title="Nicht verwendbar bei Stundenpositionen">—</DisplayAmount>
                          ) : (
                          <NumericCell
                            value={row.unit_price_vk}
                            readOnly={readOnly}
                            ariaLabel={`Material-VK Position ${row.numberLabel || row.id}`}
                            maximumFractionDigits={metalHint ? 4 : 2}
                            onCommit={(unit_price_vk) =>
                              onCommit(row, {
                                unit_price_vk,
                                surcharge_percent: surchargeForVk(row.unit_cost_ek, row.surcharge_percent, unit_price_vk),
                              })
                            }
                          />
                          )}
                        </td>
                        <td className="px-3 py-2 align-top text-right">
                          <DisplayAmount>
                            {row.isEventual ? `(${euro.format(lineTotal)})` : euro.format(lineTotal)}
                          </DisplayAmount>
                        </td>
                        <td className="px-3 py-2 align-top text-right">
                          <DisplayAmount
                            muted
                            title={rowHints ? rowHints.map((hint) => hint.title).join('\n') : undefined}
                          >
                            {rowHints ? (
                              <>
                                <AlertTriangle className="mr-1 h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
                                <span className="sr-only">Hinweis zur Kalkulation: </span>
                              </>
                            ) : null}
                            {margin == null ? '—' : `${decimal.format(margin)} %`}
                          </DisplayAmount>
                        </td>
                      </>
                    )}
                    {showRowActions ? (
                      <td className="px-2 py-2 align-top text-center">
                        <OfferCalculationRowActions
                          readOnly={readOnly}
                          canDelete={Boolean(onDeleteRow) && !isVirtualLabor}
                          deleting={isRowDeleting?.(row) ?? false}
                          onDelete={() => onDeleteRow?.(row)}
                          canSetHourlyRate={canSetHourlyRate}
                          hourlyRateOverride={hourlyRateOverride}
                          defaultRate={hourlyRate}
                          onHourlyRateChange={(hourly_rate) => onCommit(row, { hourly_rate })}
                          canToggleEventual={canToggleEventual}
                          isEventual={row.isEventual}
                          onToggleEventual={() => onToggleEventual?.(row)}
                          rowLabel={row.numberLabel || row.id}
                        />
                      </td>
                    ) : null}
                  </tr>
                    )}
                  </DraggableOfferRow>
                  {(row.components?.length ?? 0) > 0 && expandedComponentRowIds.has(row.id) ? (
                    <tr
                      ref={(node) => measureRow(row.id, 'extra', node)}
                      className="bg-slate-50"
                    >
                      <td colSpan={columnCount} className="px-3 py-2 pl-10">
                        <p className="mb-1.5 text-xs font-semibold text-app-text-muted">
                          Stückliste aus UGL ({row.components!.length} Zeilen)
                        </p>
                        <table className="w-full text-xs text-app-text">
                          <thead>
                            <tr className="text-app-text-subtle">
                              <th className="py-0.5 pr-3 text-left font-normal">Art.-Nr.</th>
                              <th className="py-0.5 pr-3 text-left font-normal">Bezeichnung</th>
                              <th className="py-0.5 pr-3 text-right font-normal">Menge</th>
                              <th className="py-0.5 pr-3 text-left font-normal">Einh.</th>
                              <th className="py-0.5 text-right font-normal">EK</th>
                            </tr>
                          </thead>
                          <tbody>
                            {row.components!.map((component) => {
                              const role = uglComponentRoleLabel(component.ugl_role, component.surcharge_type);
                              return (
                                <tr key={component.id}>
                                  <td className="py-0.5 pr-3 tabular-nums">{component.article_number || '—'}</td>
                                  <td className="py-0.5 pr-3">
                                    {component.item_name}
                                    {role ? <span className="ml-1 text-app-text-subtle">({role})</span> : null}
                                  </td>
                                  <td className="py-0.5 pr-3 text-right tabular-nums">
                                    {decimal.format(component.quantity)}
                                  </td>
                                  <td className="py-0.5 pr-3">{component.unit || ''}</td>
                                  <td className="py-0.5 text-right tabular-nums">
                                    {euro.format(component.line_total_ek)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  ) : null}
                  </React.Fragment>
                );
              })}
              {paddingBottom > 0 ? (
                <tr aria-hidden style={{ height: paddingBottom }}>
                  <td
                    colSpan={columnCount}
                    style={{ height: paddingBottom, padding: 0, border: 0, lineHeight: 0, fontSize: 0 }}
                  />
                </tr>
              ) : null}
              {end === rows.length ? renderDropBand?.(rows.length, columnCount) : null}
            </tbody>
            <tfoot className="sticky bottom-0 z-10 bg-app-surface-muted text-sm font-semibold text-app-text">
              <tr className="border-t border-app-border">
                <td colSpan={4} className="px-3 py-2 text-xs text-app-text-muted">
                  Summe
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {decimal.format(summary.laborMinutes)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{euro.format(summary.laborShare)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{euro.format(summary.materialEk)}</td>
                <td
                  className="px-3 py-2 text-right tabular-nums"
                  title="Durchschnittlicher Aufschlag über alle Positionen"
                >
                  {summary.surchargeAverage == null
                    ? '—'
                    : `Ø ${decimal.format(summary.surchargeAverage)}`}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{euro.format(summary.materialVk)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{euro.format(summary.vk)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {summary.margin == null ? '—' : `${decimal.format(summary.margin)} %`}
                </td>
                {showRowActions ? <td /> : null}
              </tr>
            </tfoot>
          </table>
        )}
        </CalculationVirtualBody>
      </div>

      <aside className="app-scrollbar flex w-full shrink-0 flex-col gap-4 xl:min-h-0 xl:w-72 xl:overflow-y-auto xl:pr-1">
        {hintGroups.length > 0 ? (
          <SidebarCard
            title="Hinweise"
            badge={
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold tabular-nums text-amber-800">
                {hintGroups.length}
              </span>
            }
            collapsed={isCollapsed('hints')}
            onToggle={() => toggleCard('hints')}
            bodyClassName="px-4 pb-4"
          >
            <MarginHintsList
              groups={hintGroups}
              onGoToRow={goToRow}
              onDismiss={readOnly ? undefined : onDismissMarginHints}
            />
          </SidebarCard>
        ) : null}
        <SidebarCard
          title="Kalkulationsübersicht"
          collapsed={isCollapsed('summary')}
          onToggle={() => toggleCard('summary')}
          bodyClassName="px-4 pb-4"
        >
          <CostCompositionChart
            materialEk={summary.materialEk}
            realLaborCosts={summary.realLaborCosts}
            vk={summary.vk}
            margin={summary.margin}
          />
          <div className="mt-4 space-y-3">
            <section>
              <p className="text-xs font-medium text-app-text-subtle">Verkauf</p>
              <dl className="mt-1.5 space-y-1.5 text-xs">
                <SummaryLine label="Material-VK" value={euro.format(summary.materialVk)} />
                <SummaryLine label="Lohnanteil" value={euro.format(summary.laborShare)} />
                <SummaryLine label="Verkauf netto" value={euro.format(summary.vk)} strong />
              </dl>
            </section>
            <section className="border-t border-app-border pt-3">
              <p className="text-xs font-medium text-app-text-subtle">Kosten</p>
              <dl className="mt-1.5 space-y-1.5 text-xs">
                <SummaryLine label="Material-EK" value={euro.format(summary.materialEk)} />
                <SummaryLine label="Reallohnkosten" value={euro.format(summary.realLaborCosts)} />
                <SummaryLine label="Gesamtkosten" value={euro.format(summary.totalCosts)} strong />
              </dl>
            </section>
            <section className="border-t border-app-border pt-3">
              <p className="text-xs font-medium text-app-text-subtle">Ergebnis</p>
              <dl className="mt-1.5 space-y-1.5 text-xs">
                <SummaryLine
                  label="Rohertrag"
                  value={euro.format(summary.profit)}
                  negative={summary.profit < 0}
                />
                <SummaryLine
                  label="Marge"
                  value={summary.margin == null ? '—' : `${decimal.format(summary.margin)} %`}
                  strong
                  negative={summary.profit < 0}
                />
              </dl>
            </section>
            <p className="border-t border-app-border pt-2 text-xs text-app-text-subtle">
              Lohnzeit: {decimal.format(summary.laborMinutes)} Min · {decimal.format(summary.laborHours)} Std
            </p>
          </div>
        </SidebarCard>
        {sidebarContent ? (
          <SidebarCard
            title="Positionen"
            collapsed={isCollapsed('positions')}
            onToggle={() => toggleCard('positions')}
            className={
              sidebarNeedsHeight && !isCollapsed('positions')
                ? 'flex h-[70vh] min-h-[24rem] flex-col'
                : ''
            }
            bodyClassName="flex min-h-0 flex-1 flex-col"
          >
            {sidebarContent}
          </SidebarCard>
        ) : null}
        <SidebarCard
          title="Kalkulationsparameter"
          collapsed={isCollapsed('rates')}
          onToggle={() => toggleCard('rates')}
          bodyClassName="space-y-3 px-4 pb-4"
        >
          <RateField
            label="Stundensatz (Standard)"
            value={hourlyRate}
            readOnly={readOnly}
            onCommit={onHourlyRateCommit}
          />
          <RateField
            label="Reallohnkosten"
            value={realLaborCostRate}
            readOnly={readOnly}
            onCommit={onRealLaborCostRateCommit}
          />
          {metalEnabled ? (
            <MetalQuotesSidebarSection
              savedSource={metalQuoteSource}
              savedCopper={copperQuoteEurPer100kg}
              savedAluminum={aluminumQuoteEurPer100kg}
              savedQuoteDate={metalQuoteDate}
              savedProcurementCostPercent={metalProcurementCostPercent}
              instanceSource={instanceMetalQuoteSource}
              instanceQuoteDate={instanceMetalQuoteDate}
              readOnly={readOnly}
              saving={metalQuotesSaving}
              onCommit={onMetalQuotesCommit}
            />
          ) : null}
        </SidebarCard>
      </aside>
    </div>
  );
}

/** Bucket in der rechten Spalte — Titelzeile klappt den Inhalt auf und zu. */
function SidebarCard({
  title,
  badge,
  collapsed,
  onToggle,
  className = '',
  bodyClassName = '',
  children,
}: {
  title: string;
  badge?: React.ReactNode;
  collapsed: boolean;
  onToggle: () => void;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`shrink-0 overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-sm ${className}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex w-full shrink-0 items-center justify-between gap-2 px-4 py-3 text-left hover:bg-app-hover"
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-sm font-semibold text-app-text">{title}</span>
          {badge}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-app-text-subtle transition-transform ${
            collapsed ? '-rotate-90' : ''
          }`}
        />
      </button>
      {collapsed ? null : <div className={bodyClassName}>{children}</div>}
    </section>
  );
}

/** Ohne „Alle anzeigen“ sichtbare Einträge der Hinweis-Karte. */
const HINTS_PREVIEW_COUNT = 4;
/** Positions-Sprungmarken je zusammengefasstem Hinweis. */
const HINT_ROW_LINKS_MAX = 12;

/** Hinweise zur Kalkulation: blockieren nichts, lassen sich anspringen und ausblenden. */
function MarginHintsList({
  groups,
  onGoToRow,
  onDismiss,
}: {
  groups: OfferMarginHintGroup[];
  onGoToRow: (rowIndex: number) => void;
  onDismiss?: (ids: string[]) => void;
}) {
  const [showAll, setShowAll] = React.useState(false);
  const visible = showAll ? groups : groups.slice(0, HINTS_PREVIEW_COUNT);
  const hiddenCount = groups.length - visible.length;
  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {visible.map((group) => (
          <MarginHintItem key={group.key} group={group} onGoToRow={onGoToRow} onDismiss={onDismiss} />
        ))}
      </ul>
      {groups.length > HINTS_PREVIEW_COUNT || (onDismiss && groups.length > 1) ? (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          {groups.length > HINTS_PREVIEW_COUNT ? (
            <button
              type="button"
              onClick={() => setShowAll((value) => !value)}
              className="text-xs font-medium text-pipedrive-blue hover:underline"
            >
              {showAll ? 'Weniger anzeigen' : `${hiddenCount} weitere anzeigen`}
            </button>
          ) : (
            <span />
          )}
          {onDismiss && groups.length > 1 ? (
            <button
              type="button"
              onClick={() => onDismiss(groups.flatMap((group) => group.hints.map((hint) => hint.id)))}
              className="text-xs font-medium text-app-text-muted hover:text-app-text"
            >
              Alle ausblenden
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MarginHintItem({
  group,
  onGoToRow,
  onDismiss,
}: {
  group: OfferMarginHintGroup;
  onGoToRow: (rowIndex: number) => void;
  onDismiss?: (ids: string[]) => void;
}) {
  const warn = group.severity === 'warn';
  const rowHints = group.hints.filter((hint) => hint.rowIndex != null);
  const single = group.hints.length === 1 ? group.hints[0] : undefined;
  return (
    <li
      className={`rounded-md border px-3 py-2 ${
        warn ? 'border-amber-200 bg-amber-50/70' : 'border-app-border bg-app-surface-muted'
      }`}
    >
      <div className="flex items-start gap-2">
        {warn ? (
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
        ) : (
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-app-text-subtle" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-app-text">{group.title}</p>
          {group.detail ? <p className="mt-0.5 text-xs leading-snug text-app-text-muted">{group.detail}</p> : null}
          {!single && rowHints.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {rowHints.slice(0, HINT_ROW_LINKS_MAX).map((hint) => (
                <button
                  key={hint.id}
                  type="button"
                  onClick={() => onGoToRow(hint.rowIndex!)}
                  title={hint.title}
                  className="rounded border border-app-border bg-app-surface px-1.5 py-0.5 text-[11px] tabular-nums text-app-text-muted hover:bg-app-hover hover:text-app-text"
                >
                  {hint.numberLabel ? `Pos. ${hint.numberLabel}` : `Zeile ${hint.rowIndex! + 1}`}
                </button>
              ))}
              {rowHints.length > HINT_ROW_LINKS_MAX ? (
                <span className="px-1 py-0.5 text-[11px] text-app-text-subtle">
                  +{rowHints.length - HINT_ROW_LINKS_MAX}
                </span>
              ) : null}
            </div>
          ) : null}
          {(single?.rowIndex != null) || onDismiss ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
              {single?.rowIndex != null ? (
                <button
                  type="button"
                  onClick={() => onGoToRow(single.rowIndex!)}
                  className="text-xs font-medium text-pipedrive-blue hover:underline"
                >
                  Zur Position
                </button>
              ) : null}
              {onDismiss ? (
                <button
                  type="button"
                  onClick={() => onDismiss(group.hints.map((hint) => hint.id))}
                  className="text-xs font-medium text-app-text-muted hover:text-app-text"
                >
                  Ausblenden
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/** Titelzeile: Zwischenrechnung über alle Positionen des Titels, Spalten wie im Tabellenfuß. */
function TitleSubtotalCells({ totals }: { totals: OfferCalculationTitleTotals }) {
  const total = totals.materialVk + totals.laborShare;
  const margin = marginPercent(totals.costs, total);
  const surchargeAverage =
    totals.surchargeCount > 0 ? totals.surchargeSum / totals.surchargeCount : null;
  return (
    <>
      <td colSpan={2} />
      <td className="px-3 py-2 align-top text-right">
        <DisplayAmount muted>{decimal.format(totals.laborMinutes)}</DisplayAmount>
      </td>
      <td className="px-3 py-2 align-top text-right">
        <DisplayAmount muted>{euro.format(totals.laborShare)}</DisplayAmount>
      </td>
      <td className="px-3 py-2 align-top text-right">
        <DisplayAmount muted>{euro.format(totals.materialEk)}</DisplayAmount>
      </td>
      <td
        className="px-3 py-2 align-top text-right"
        title="Durchschnittlicher Aufschlag in diesem Titel"
      >
        <DisplayAmount muted>
          {surchargeAverage == null ? '—' : `Ø ${decimal.format(surchargeAverage)}`}
        </DisplayAmount>
      </td>
      <td className="px-3 py-2 align-top text-right">
        <DisplayAmount muted>{euro.format(totals.materialVk)}</DisplayAmount>
      </td>
      <td className="px-3 py-2 align-top text-right" title="Titelsumme">
        <DisplayAmount>{euro.format(total)}</DisplayAmount>
      </td>
      <td className="px-3 py-2 align-top text-right">
        <DisplayAmount muted>
          {margin == null ? '—' : `${decimal.format(margin)} %`}
        </DisplayAmount>
      </td>
    </>
  );
}

function DisplayAmount({
  children,
  muted = false,
  title,
}: {
  children: React.ReactNode;
  muted?: boolean;
  title?: string;
}) {
  return (
    <div
      title={title}
      className={`flex h-9 items-center justify-end text-sm font-semibold tabular-nums ${
        muted ? 'text-app-text-muted' : 'text-app-text'
      }`}
    >
      {children}
    </div>
  );
}

function SummaryLine({
  label,
  value,
  strong = false,
  negative = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
  negative?: boolean;
}) {
  const valueTone = negative
    ? 'text-red-600'
    : strong
      ? 'text-app-text'
      : 'text-app-text-muted';
  return (
    <div className="flex items-center justify-between gap-4 text-app-text-subtle">
      <dt className={strong ? 'font-medium text-app-text-muted' : undefined}>{label}</dt>
      <dd className={`tabular-nums ${strong ? 'font-semibold' : ''} ${valueTone}`}>{value}</dd>
    </div>
  );
}

const COST_SLICE_COLORS = {
  material: '#3b82f6',
  labor: '#f59e0b',
  margin: '#10b981',
} as const;

type CostSlice = { key: string; label: string; value: number; color: string };

function CostSliceTooltip({
  base,
  active,
  payload,
}: {
  base: number;
  active?: boolean;
  payload?: Array<{ payload: CostSlice }>;
}) {
  const slice = payload?.[0]?.payload;
  if (!active || !slice) return null;
  return (
    <div className="rounded-md border border-app-border bg-app-surface px-2.5 py-1.5 text-xs shadow-lg">
      <div className="flex items-center gap-1.5 font-medium text-app-text">
        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: slice.color }} />
        {slice.label}
      </div>
      <div className="mt-0.5 tabular-nums text-app-text-muted">
        {euro.format(slice.value)} · {decimal.format((slice.value / base) * 100)} %
      </div>
    </div>
  );
}

/** Segmentnamen direkt am Ring statt in einer Legende darunter. */
function renderSliceLabel(props: unknown) {
  const { cx, cy, midAngle, outerRadius, payload } = props as {
    cx: number;
    cy: number;
    midAngle: number;
    outerRadius: number;
    payload: CostSlice;
  };
  const radians = -midAngle * (Math.PI / 180);
  const x = cx + (outerRadius + 10) * Math.cos(radians);
  const y = cy + (outerRadius + 10) * Math.sin(radians);
  return (
    <text
      x={x}
      y={y}
      fill={payload.color}
      fontSize={12}
      fontWeight={600}
      textAnchor={x >= cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {payload.label}
    </text>
  );
}

/** Zerlegt den Nettoverkauf in Material-EK, Reallohnkosten und Deckungsbeitrag. */
function CostCompositionChart({
  materialEk,
  realLaborCosts,
  vk,
  margin,
}: {
  materialEk: number;
  realLaborCosts: number;
  vk: number;
  margin: number | null;
}) {
  const profit = vk - materialEk - realLaborCosts;
  const slices = React.useMemo<CostSlice[]>(
    () =>
      [
        { key: 'material', label: 'Material', value: Math.max(0, materialEk), color: COST_SLICE_COLORS.material },
        { key: 'labor', label: 'Lohn', value: Math.max(0, realLaborCosts), color: COST_SLICE_COLORS.labor },
        { key: 'margin', label: 'Marge', value: Math.max(0, profit), color: COST_SLICE_COLORS.margin },
      ].filter((slice) => slice.value > 0.0001),
    [materialEk, profit, realLaborCosts],
  );

  const base = slices.reduce((sum, slice) => sum + slice.value, 0);

  if (base <= 0.0001) {
    return <p className="mt-4 text-center text-xs text-app-text-subtle">Noch keine Werte zum Aufteilen.</p>;
  }

  return (
    <div className="mt-2">
      <div className="relative h-[160px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={56}
              paddingAngle={slices.length > 1 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
              labelLine={false}
              label={renderSliceLabel}
            >
              {slices.map((slice) => (
                <Cell key={slice.key} fill={slice.color} />
              ))}
            </Pie>
            <Tooltip
              cursor={false}
              wrapperStyle={{ outline: 'none', zIndex: 30 }}
              content={<CostSliceTooltip base={base} />}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-xs font-medium text-app-text-subtle">Marge</span>
          <span className={`text-sm font-bold tabular-nums ${profit < 0 ? 'text-red-600' : 'text-app-text'}`}>
            {margin == null ? '—' : `${Math.round(margin)} %`}
          </span>
        </div>
      </div>
      {profit < -0.0001 ? (
        <p className="mt-1 text-center text-xs font-semibold text-red-600">
          Unterdeckung {euro.format(profit)}
        </p>
      ) : null}
    </div>
  );
}

function RateField({
  label,
  value,
  readOnly,
  onCommit,
}: {
  label: string;
  value: number;
  readOnly: boolean;
  onCommit: (value: number) => void;
}) {
  const [text, setText] = React.useState(() => decimal.format(value));
  React.useEffect(() => setText(decimal.format(value)), [value]);

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-app-text-muted">{label}</span>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={text}
          disabled={readOnly}
          onChange={(event) => setText(event.target.value)}
          onFocus={(event) => {
            if (parseGermanNumber(text) === 0) setText('');
            else event.target.select();
          }}
          onBlur={() => {
            const next = Math.max(0, parseGermanNumber(text));
            setText(decimal.format(next));
            if (Math.abs(next - value) > 0.0001) onCommit(next);
          }}
          className={`h-9 w-full rounded-md border border-app-border bg-app-surface py-0 pl-3 pr-12 text-right text-sm tabular-nums text-app-text disabled:bg-app-surface-muted disabled:text-app-text-subtle ${CELL_FOCUS_CLASS}`}
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-app-text-subtle">
          €/h
        </span>
      </div>
    </label>
  );
}

/**
 * Metallnotierungen des Angebots. Änderungen bleiben lokal, bis „Speichern“ gedrückt wird —
 * jedes Speichern rechnet serverseitig alle Positionen neu.
 */
function formatQuoteDateDe(value: string | null): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-');
  return `${day}.${month}.${year}`;
}

function MetalQuotesSidebarSection({
  savedSource,
  savedCopper,
  savedAluminum,
  savedQuoteDate,
  savedProcurementCostPercent,
  instanceSource,
  instanceQuoteDate,
  readOnly,
  saving,
  onCommit,
}: {
  savedSource: MetalQuoteSource;
  savedCopper: number | null;
  savedAluminum: number | null;
  savedQuoteDate: string | null;
  savedProcurementCostPercent: number;
  instanceSource: MetalQuoteSource;
  instanceQuoteDate: string | null;
  readOnly: boolean;
  saving: boolean;
  onCommit: (values: MetalQuoteValues) => void;
}) {
  const [draft, setDraft] = React.useState<MetalQuoteValues>({
    source: savedSource,
    copper: savedCopper,
    aluminum: savedAluminum,
    quoteDate: savedQuoteDate,
    procurementCostPercent: savedProcurementCostPercent,
  });
  const [suggesting, setSuggesting] = React.useState(false);
  const [note, setNote] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDraft({
      source: savedSource,
      copper: savedCopper,
      aluminum: savedAluminum,
      quoteDate: savedQuoteDate,
      procurementCostPercent: savedProcurementCostPercent,
    });
    setNote(null);
  }, [savedSource, savedCopper, savedAluminum, savedQuoteDate, savedProcurementCostPercent]);

  const dirty =
    draft.source !== savedSource ||
    draft.copper !== savedCopper ||
    draft.aluminum !== savedAluminum ||
    draft.quoteDate !== savedQuoteDate ||
    draft.procurementCostPercent !== savedProcurementCostPercent;

  const disabled = readOnly || saving;
  const inherited = draft.source === instanceSource;
  const contextDate = formatQuoteDateDe(draft.quoteDate ?? instanceQuoteDate);

  const suggestCurrentQuote = async () => {
    setSuggesting(true);
    setError(null);
    setNote(null);
    try {
      const suggestion = await fetchCurrentWestmetallQuote();
      setDraft((prev) => ({
        ...prev,
        source: 'westmetall',
        copper: suggestion.copperQuoteEurPer100kg,
        quoteDate: suggestion.quoteDate,
      }));
      setNote(
        suggestion.note ||
          'Die vorgeschlagene Notierung ist noch nicht gespeichert. Bitte prüfen und speichern.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Aktuelle WM-Notiz konnte nicht geladen werden.');
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-app-border pt-3">
      <p className="text-xs text-app-text-subtle">
        Grundlage: {METAL_QUOTE_SOURCE_LABELS[draft.source]}
        {contextDate ? ` vom ${contextDate}` : ''}
        {inherited ? ' — aus den Einstellungen' : null}
        {' · '}
        <Link to={SETTINGS_PATH_INTEGRATION_METAL} className="text-pipedrive-blue hover:underline">
          Einstellungen
        </Link>
      </p>
      <MetalQuoteField
        label={copperQuoteFieldLabel(draft.source)}
        value={draft.copper}
        readOnly={disabled}
        onCommit={(value) => setDraft((prev) => ({ ...prev, source: 'manual', copper: value }))}
      />
      <MetalQuoteField
        label={aluminumQuoteFieldLabel(draft.source)}
        value={draft.aluminum}
        readOnly={disabled}
        onCommit={(value) => setDraft((prev) => ({ ...prev, source: 'manual', aluminum: value }))}
      />
      <MetalQuoteField
        label="Bezugskosten"
        value={draft.procurementCostPercent}
        readOnly={disabled}
        onCommit={(value) => setDraft((prev) => ({ ...prev, procurementCostPercent: value ?? 0 }))}
        suffix="%"
      />
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-app-text-muted">Notierungsdatum</span>
        <DatePicker
          value={draft.quoteDate ?? ''}
          onChange={(value) => setDraft((prev) => ({ ...prev, quoteDate: value || null }))}
          disabled={disabled}
          inputClassName="text-right"
          menuZIndex={UI_Z_INDEX.portalDropdown}
        />
      </label>
      <button
        type="button"
        onClick={() => void suggestCurrentQuote()}
        disabled={disabled || suggesting}
        className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-app-border bg-app-surface px-3.5 text-xs font-medium text-app-text-muted hover:bg-app-hover disabled:cursor-not-allowed disabled:opacity-60"
      >
        {suggesting ? <LoadingSpinner size="xs" /> : null}
        Aktuelle WM-Kupfernotiz übernehmen
      </button>
      <button
        type="button"
        onClick={() => onCommit(draft)}
        disabled={disabled || !dirty}
        className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-pipedrive-blue px-3.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving ? <LoadingSpinner size="xs" /> : null}
        Notierungen speichern
      </button>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
      {note ? <p className="text-xs text-app-text-subtle">{note}</p> : null}
      {dirty && !error ? (
        <p className="text-xs text-app-text-subtle">
          Noch nicht gespeichert — beim Speichern werden alle Positionen neu berechnet.
        </p>
      ) : null}
    </div>
  );
}

function MetalQuoteField({
  label,
  value,
  readOnly,
  onCommit,
  suffix = '€/100 kg',
}: {
  label: string;
  value: number | null;
  readOnly: boolean;
  onCommit: (value: number | null) => void;
  suffix?: string;
}) {
  const [text, setText] = React.useState(() => (value == null ? '' : decimal.format(value)));
  React.useEffect(() => setText(value == null ? '' : decimal.format(value)), [value]);

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-app-text-muted">{label}</span>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={text}
          disabled={readOnly}
          placeholder="Nicht gesetzt"
          onChange={(event) => setText(event.target.value)}
          onFocus={(event) => event.target.select()}
          onBlur={() => {
            const trimmed = text.trim();
            const next = trimmed ? Math.max(0, parseGermanNumber(trimmed)) : null;
            setText(next == null ? '' : decimal.format(next));
            if (next !== value) onCommit(next);
          }}
          className={`h-9 w-full rounded-md border border-app-border bg-app-surface py-0 pl-3 pr-20 text-right text-sm tabular-nums text-app-text placeholder:text-app-text-subtle disabled:bg-app-surface-muted disabled:text-app-text-subtle ${CELL_FOCUS_CLASS}`}
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-app-text-subtle">
          {suffix}
        </span>
      </div>
    </label>
  );
}
